import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FireIcon, ArrowPathIcon, ArrowTrendingUpIcon, Cog6ToothIcon, ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, Legend,
} from 'recharts';
import { getHypeTrends, HypeTrend, HypeTrendsResponse } from '../services/hype';
import TrendDetailModal from '../components/hype/TrendDetailModal';

const MOMENTUM: Record<string, { label: string; cls: string; color: string }> = {
  surging: { label: 'Surging', cls: 'bg-orange-50 text-orange-700 ring-1 ring-orange-200', color: '#f97316' },
  rising:  { label: 'Rising',  cls: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200', color: '#10b981' },
  steady:  { label: 'Steady',  cls: 'bg-slate-50 text-slate-600 ring-1 ring-slate-200', color: '#94a3b8' },
  cooling: { label: 'Cooling', cls: 'bg-blue-50 text-blue-600 ring-1 ring-blue-200', color: '#3b82f6' },
};

// distinct line colors for the multi-line 24h chart
const LINE_COLORS = ['#8b5cf6', '#f97316', '#10b981', '#3b82f6', '#ec4899', '#eab308'];

function fmtViews(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return `${Math.round(n)}`;
}

function FreshnessBadge({ pct }: { pct: number }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-orange-600">
      <FireIcon className="h-3.5 w-3.5" />{pct.toFixed(0)}% fresh
      <span className="text-gray-400 font-normal">last 6h</span>
    </span>
  );
}

function DirectionBadge({ delta }: { delta: number | null }) {
  if (delta == null) return <span className="text-xs font-medium text-violet-600">● new</span>;
  if (Math.abs(delta) < 1) return <span className="text-xs text-gray-400">▬ flat</span>;
  const up = delta > 0;
  return (
    <span className={`text-xs font-semibold ${up ? 'text-emerald-600' : 'text-red-500'}`}>
      {up ? '▲' : '▼'} {Math.abs(delta).toFixed(0)}
    </span>
  );
}

function hoursLabel(hours: number[]): string {
  if (!hours?.length) return '—';
  return hours.map(h => `${String(h).padStart(2, '0')}:00`).join(', ');
}

function Kpi({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-xl font-semibold text-gray-900 mt-1 truncate">{value ?? '—'}</p>
    </div>
  );
}

function TrendCard({ trend, rank, onOpen }: { trend: HypeTrend; rank: number; onOpen: () => void }) {
  const m = MOMENTUM[trend.momentum] || MOMENTUM.steady;
  return (
    <div onClick={onOpen}
      className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 hover:shadow-md hover:border-violet-200 transition-all cursor-pointer">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="text-sm font-bold text-gray-300 mt-0.5 w-6 shrink-0">#{rank}</span>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-900 truncate">{trend.label}</h3>
            <div className="mt-1"><FreshnessBadge pct={trend.change_24h} /></div>
          </div>
        </div>
        <span className={`px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap ${m.cls}`}>{m.label}</span>
      </div>

      {/* 24h sparkline */}
      {trend.series?.length > 1 && (
        <div className="mt-3 -mx-1" style={{ height: 44 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend.series} margin={{ top: 4, bottom: 0, left: 4, right: 4 }}>
              <Line type="monotone" dataKey="value" stroke={m.color} strokeWidth={2} dot={false} isAnimationActive={false} />
              <YAxis domain={[0, 100]} hide />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* HypeScore bar */}
      <div className="mt-2">
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
          <span className="flex items-center gap-1.5">Hype Score <DirectionBadge delta={trend.delta_score} /></span>
          <span className="font-semibold text-gray-900">{trend.hype_score.toFixed(0)}/100</span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full" style={{ width: `${trend.hype_score}%`, background: m.color }} />
        </div>
      </div>

      {/* trending terms in this category */}
      {trend.terms?.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {trend.terms.slice(0, 5).map(t => (
            <span key={t.term}
              className={`px-2 py-0.5 text-xs rounded-md ${t.type === 'hashtag'
                ? 'bg-violet-50 text-violet-700' : 'bg-gray-50 text-gray-600'}`}>
              {t.type === 'hashtag' ? '#' : ''}{t.term}
            </span>
          ))}
        </div>
      )}

      {/* top viral content */}
      {trend.examples?.length > 0 && (
        <div className="mt-3 space-y-1.5 border-t border-gray-50 pt-2.5">
          {trend.examples.slice(0, 2).map((ex, i) => (
            <a key={i} href={ex.url || '#'} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
               className="flex items-center justify-between gap-2 group">
              <span className="text-xs text-gray-600 truncate group-hover:text-violet-700 flex items-center gap-1">
                {ex.title}<ArrowTopRightOnSquareIcon className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-100" />
              </span>
              {ex.metrics?.views > 0 && (
                <span className="text-xs text-gray-400 whitespace-nowrap">{fmtViews(ex.metrics.views)} views</span>
              )}
            </a>
          ))}
        </div>
      )}

      <p className="text-xs text-violet-600 mt-3 font-medium">View content ideas &amp; ROI →</p>
    </div>
  );
}

export default function HypePage() {
  const [data, setData] = useState<HypeTrendsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openTrend, setOpenTrend] = useState<string | null>(null);

  const load = async (refresh = false) => {
    refresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      setData(await getHypeTrends(refresh));
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not load trends.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const trends = data?.trends || [];
  const top = trends.slice(0, 6);

  // merge each trend's 24h series by position into one dataset for the multi-line chart
  const maxLen = Math.max(0, ...top.map(t => t.series?.length || 0));
  const lineData = Array.from({ length: maxLen }, (_, i) => {
    const row: Record<string, any> = { t: top.find(t => t.series?.[i])?.series[i]?.t || '' };
    top.forEach(t => { const p = t.series?.[i]; if (p) row[t.label] = p.value; });
    return row;
  });
  const momentumData = trends.slice(0, 10).map(t => ({
    name: t.label.length > 16 ? t.label.slice(0, 16) + '…' : t.label,
    change: Math.round(t.change_24h),
    color: (MOMENTUM[t.momentum] || MOMENTUM.steady).color,
  }));

  const ov = data?.overview;
  const termData = (ov?.top_terms || []).slice(0, 10).map(t => ({
    name: (t.type === 'hashtag' ? '#' : '') + t.term,
    score: t.score,
    color: t.type === 'hashtag' ? '#8b5cf6' : '#94a3b8',
  }));

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-violet-50 flex items-center justify-center">
            <FireIcon className="h-6 w-6 text-violet-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Hype</h1>
            <p className="text-sm text-gray-500">
              Momentum over the last 24h{data?.config?.city ? ` in ${data.config.city}` : ''}, by category.
            </p>
          </div>
        </div>
        <button onClick={() => load(true)} disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 disabled:opacity-60">
          <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 text-gray-400">
          <ArrowPathIcon className="h-8 w-8 animate-spin mb-3" />
          <p className="text-sm">Analyzing the last 24 hours…</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-100 text-red-600 rounded-xl p-4 text-sm">{error}</div>
      ) : trends.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-10 text-center">
          <ArrowTrendingUpIcon className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-gray-900">No trends yet</h3>
          <p className="text-sm text-gray-500 mt-1 mb-4">Pick your location and categories so we know what to track.</p>
          <Link to="/settings"
            className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700">
            <Cog6ToothIcon className="h-4 w-4" /> Configure Hype
          </Link>
        </div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Kpi label="Trends tracked" value={ov?.kpis.trend_count} />
            <Kpi label="Videos analyzed" value={ov?.kpis.video_count} />
            <Kpi label="Total views (24h)" value={ov?.kpis.total_views != null ? fmtViews(ov.kpis.total_views) : '—'} />
            <Kpi label="Top category" value={ov?.kpis.top_category} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            {/* 24h interest curves */}
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Viral content activity (last 24h)</h2>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={lineData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <XAxis dataKey="t" tick={{ fontSize: 10 }} stroke="#cbd5e1" interval="preserveStartEnd" minTickGap={40} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} stroke="#cbd5e1" />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  {top.map((t, i) => (
                    <Line key={t.trend_id} type="monotone" dataKey={t.label} stroke={LINE_COLORS[i % LINE_COLORS.length]}
                      strokeWidth={2} dot={false} connectNulls isAnimationActive={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* 24h momentum */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Freshness · % of buzz in last 6h</h2>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={momentumData} layout="vertical" margin={{ left: 4, right: 16 }}>
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} stroke="#cbd5e1" unit="%" />
                  <YAxis type="category" dataKey="name" width={96} tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <Tooltip cursor={{ fill: '#f8fafc' }} formatter={(v) => [`${v}%`, 'fresh (6h)']} />
                  <Bar dataKey="change" radius={[0, 4, 4, 0]} barSize={14}>
                    {momentumData.map((d, i) => <Cell key={i} fill={d.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Keywords/hashtags + leaderboard */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {/* trending keywords & hashtags */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-1">Keywords &amp; hashtags picking up</h2>
              <p className="text-xs text-gray-400 mb-3">Across your categories · purple = hashtag</p>
              {termData.length > 0 ? (
                <ResponsiveContainer width="100%" height={Math.max(180, termData.length * 26)}>
                  <BarChart data={termData} layout="vertical" margin={{ left: 4, right: 16 }}>
                    <XAxis type="number" domain={[0, 100]} hide />
                    <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                    <Tooltip cursor={{ fill: '#f8fafc' }} formatter={(v) => [`${v}/100`, 'momentum']} />
                    <Bar dataKey="score" radius={[0, 4, 4, 0]} barSize={14}>
                      {termData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="text-sm text-gray-400 py-8 text-center">No tagged content yet.</p>}
            </div>

            {/* top viral content leaderboard */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Top viral content (last 24h)</h2>
              <ol className="space-y-2.5">
                {(ov?.leaderboard || []).map((v, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="text-sm font-bold text-gray-300 w-5 shrink-0">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <a href={v.url || '#'} target="_blank" rel="noreferrer"
                         className="text-sm text-gray-700 hover:text-violet-700 truncate block">{v.title}</a>
                      <p className="text-xs text-gray-400 truncate">
                        {v.author || '—'} · <span className="text-violet-600">{v.category}</span>
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-xs font-medium text-gray-900">{fmtViews(v.views)}</p>
                      <p className="text-xs text-gray-400">{fmtViews(v.velocity)}/hr{v.engagement ? ` · ${v.engagement.toFixed(1)}%` : ''}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>

          {/* Creators to watch + format & timing */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Creators to watch</h2>
              {(ov?.creators || []).length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                  {(ov?.creators || []).slice(0, 8).map((c, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 border-b border-gray-50 py-1.5">
                      <div className="min-w-0">
                        <p className="text-sm text-gray-800 truncate">{c.name}</p>
                        <p className="text-xs text-gray-400 truncate">{c.category} · {c.count} videos</p>
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap">{fmtViews(c.views)} views</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-sm text-gray-400 py-6 text-center">No creator data yet.</p>}
            </div>

            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-3">Format &amp; timing</h2>
              <p className="text-xs text-gray-500 mb-1">Content mix (by momentum)</p>
              <div className="flex h-4 rounded-full overflow-hidden mb-1">
                <div className="bg-violet-500" style={{ width: `${ov?.format_mix?.short_pct ?? 0}%` }} />
                <div className="bg-slate-300" style={{ width: `${ov?.format_mix?.long_pct ?? 0}%` }} />
              </div>
              <div className="flex justify-between text-xs text-gray-500 mb-4">
                <span className="text-violet-600 font-medium">{ov?.format_mix?.short_pct ?? 0}% Shorts</span>
                <span>{ov?.format_mix?.long_pct ?? 0}% Long-form</span>
              </div>
              <p className="text-xs text-gray-500">Best time to post</p>
              <p className="text-base font-semibold text-gray-900 mt-0.5">{hoursLabel(ov?.best_hours || [])}</p>
              <p className="text-xs text-gray-400 mt-0.5">when viral content peaks (local hour)</p>
            </div>
          </div>

          {/* Category cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {trends.map((t, i) => (
              <TrendCard key={t.trend_id || i} trend={t} rank={i + 1} onOpen={() => setOpenTrend(t.trend_id)} />
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-6 text-center">
            Updated {data?.generated_at ? new Date(data.generated_at).toLocaleString() : ''}
          </p>
        </>
      )}

      {openTrend && <TrendDetailModal trendId={openTrend} onClose={() => setOpenTrend(null)} />}
    </div>
  );
}
